import { createTheme, Grid, IconButton, ThemeProvider, Typography } from '@material-ui/core';
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { Arrow, ChatCardIcon, ChatHistoryIcon, NonSuccessCriteriaIcon } from 'src/components/Icons';
import { handleDevicePulseClicked, toBrowserTimeZone } from 'src/app/utils';
import { useState, useEffect } from 'react';
import { Loader } from '../maintenance/test-automation/components/Loader';
import { ToasterComponent } from '../maintenance/test-automation/components/Toaster';
import { fetchChatSessions, getChatHistory } from '../maintenance/test-automation/components/apiHelper';
import useStyles from '../maintenance/assets/styles';
import Cookies from "js-cookie";

type ChatSession = {
    creationDate: string;
    deviceId: string;
    title: string;
    notification: string;
    batch_number: string;
};

export const ChatHistoryComponent = () => {
    const [chatData, setChatData] = useState({
        today: { count: 0, sessions: [] },
        yesterday: { count: 0, sessions: [] },
        previous30Days: { count: 0, sessions: [] },
    });
    const [loading, setLoading] = useState(false);
    const [isToasterOpen, setIsToasterOpen] = useState(false)
    const [toasterMessage, setToasterMessage] = useState("")
    const [reqSuccess, setReqSuccess] = useState(false)

    const userId = Cookies.get('USER_ID');
    const referenceId = Cookies.get('ACCOUNT');

    const theme = createTheme({
        typography: {
          fontFamily: "'Poppins', sans-serif",
        },
    });
    const history = useHistory();
    const classes = useStyles();

    const handleCardClick = async (sessionId, batchNumber, isNotification = true) => {
      setLoading(true);
    
      try {
        let requestBody;
    
        if (isNotification) {
          const [referenceId, userId, rootCauseId] = sessionId.split('_');
    
          requestBody = {
            referenceId,
            userId,
            rootCauseId,
          };
        } else {
          requestBody = {
            sessionId,
          };
        }
    
        const data = await getChatHistory(requestBody);
    
        history.push(`/ChatHistory/ChatBot/${isNotification ? sessionId.split('_')[2] : sessionId}`, {
          chatbotData: data,
          batchNumber,
          rootCauseId: isNotification ? sessionId.split('_')[2] : sessionId,
          isNotification,
        });
    
      } catch (error) {
        let errorMessage = 'An unexpected error occurred';
    
        if (error.response && error.response.status) {
          const statusCode = error.response.status;
    
          if (statusCode === 500) {
            errorMessage = 'Something went wrong.';
          } else if (statusCode === 422) {
            errorMessage = error.response.data.message;
          }
        }
    
        setIsToasterOpen(true);
        setReqSuccess(false);
        setToasterMessage(errorMessage);
        setTimeout(() => {
          setIsToasterOpen(false);
        }, 5000);
      } finally {
        setLoading(false);
      }
      
    };

    const getAllChatSessions = async () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
  
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
  
      const isSameDate = (date1: Date, date2: Date) =>
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
    
      const groupByDateRange = (sessions: any[]) => {
        const validSessions = sessions.filter(session => 
          session && session.creationDate && new Date(session.creationDate).toString() !== 'Invalid Date'
        );
    
        if (validSessions.length === 0) {
          return {
            today: [],
            yesterday: [],
            previous30Days: []
          };
        }
    
        const sortedSessions = validSessions.sort((a, b) => 
          new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
        );
    
        return {
          today: sortedSessions.filter(session => isSameDate(new Date(session.creationDate), today)),
          yesterday: sortedSessions.filter(session => isSameDate(new Date(session.creationDate), yesterday)),
          previous30Days: sortedSessions.filter(session => {
            const sessionDate = new Date(session.creationDate);
            return sessionDate >= thirtyDaysAgo && sessionDate < yesterday;
          }),
        };
      };
    
      try {
        setLoading(true);
        const response = await fetchChatSessions(referenceId, userId);
    
        if (!response.data) {
          return {
            today: { count: 0, sessions: [] },
            yesterday: { count: 0, sessions: [] },
            previous30Days: { count: 0, sessions: [] },
          };
        }
    
        const notificationSessions = response.data.notification_chat_list 
          ? Object.entries(response.data.notification_chat_list).map(([key, value]: [string, any]) => {
              if (!value.creationDate) return null;
              
              const formattedDate = toBrowserTimeZone(value.creationDate);
              if (!formattedDate) return null;

              let notificationTitle = value.title;
              try{
                const issueMatch = value.title.match(/ - (.+?)(\)|,)/);
                if(issueMatch && issueMatch[1]) {
                  notificationTitle = `The ${value.deviceId} failed due to ${issueMatch[1]}`;
                }
              } catch (e) {
                  console.error('Error formatting notification title:', e);
                  notificationTitle = `The ${value.deviceId} failed due to - ${value.title}`;
              }
    
              return {
                ...value,
                creationDate: formattedDate,
                id: key,
                title: notificationTitle,
                description: value.notification,
                batchNumber: value.batch_number,
                isNotification: true,
              };
            }).filter(Boolean)
          : [];

        const commonChatSessions = response.data.common_chat_list
          ? Object.entries(response.data.common_chat_list).map(([key, value]: [string, any]) => {
              if (!value.creationDate) return null;
              
              const formattedDate = toBrowserTimeZone(value.creationDate);
              if (!formattedDate) return null;

              let cleanedTitle = value.title;
              if (typeof value.title === 'string') {
                cleanedTitle = value.title.replace(/^["'`]+|["'`]+$/g, '');
                cleanedTitle = cleanedTitle.trim();
              }

              return {
                ...value,
                creationDate: formattedDate,
                id: key,
                title: cleanedTitle,
                description: '', 
                batchNumber: '',
                isNotification: false,
              };
            }).filter(Boolean)
          : [];
    
        const sessions = [...notificationSessions, ...commonChatSessions];
        
        if (sessions.length === 0) {
          return {
            today: { count: 0, sessions: [] },
            yesterday: { count: 0, sessions: [] },
            previous30Days: { count: 0, sessions: [] },
          };
        }
    
        const grouped = groupByDateRange(sessions);
    
        return {
          today: { count: grouped.today.length, sessions: grouped.today },
          yesterday: { count: grouped.yesterday.length, sessions: grouped.yesterday },
          previous30Days: { count: grouped.previous30Days.length, sessions: grouped.previous30Days },
        };
      } catch (error) {
        console.error('Error fetching chat sessions:', error);
        let errorMessage = 'An unexpected error occurred';
    
        if (error.response && error.response.status) {
          const statusCode = error.response.status;
    
          if (statusCode === 500) {
            errorMessage = 'Something went wrong.';
          } else if (statusCode === 422) {
            errorMessage = error.response.data.message;
          } else if(statusCode === 404) {
            errorMessage = error.response.data.detail;
          }
        }
        setIsToasterOpen(true);
        setReqSuccess(false);
        setToasterMessage(errorMessage);
        setTimeout(() => {
          setIsToasterOpen(false);
        }, 5000);
        return {
          today: { count: 0, sessions: [] },
          yesterday: { count: 0, sessions: [] },
          previous30Days: { count: 0, sessions: [] },
        };
      } finally {
        setLoading(false);
      }
    };
  

    useEffect(() => {
        const fetchChatSessions = async () => {
          const data = await getAllChatSessions();
          console.log("Chat Data", data);
          if (data) {
            setChatData(data);
          }
        };
    
        fetchChatSessions();
    }, []);
      

    return (
      <ThemeProvider theme={theme}>
        <Grid container style={{ padding: '25px' }}>
          <Grid item xs={12} md={12}>
            <Grid className={classes.pageTitle} style={{margin:'0px'}}>Chat History</Grid>
          <Grid
            container
            className={classes.redirectContainer}
          >
            <Grid
              item
              style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}
            >
              
              <Link to={'/TestAutomation'} style={{ paddingLeft: '10px', textDecoration: 'none' }}>
              <IconButton onClick={handleDevicePulseClicked} style={{padding:"0px", marginBottom:"1px"}}>
                <NonSuccessCriteriaIcon />
              </IconButton>
              </Link>
            </Grid>
            <Arrow />
            <Grid item style={{ display: 'flex', alignItems: 'center' }}>
              <ChatHistoryIcon />
              <Typography className={classes.redirectTitle}>Chat History</Typography>
            </Grid>
          </Grid>
    
          <Grid
            container
            className={classes.backgroundContainer}
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            {['today', 'yesterday', 'previous30Days'].map((key) => {
              if (chatData[key].count > 0) {
                return (
                  <div key={key} style={{margin:'0px'}}>
                    <Grid
                      item
                      className={classes.chatCardGrid}
                    >
                      <Typography
                        className={classes.chatCardDay}
                      >
                        {key === 'today'
                          ? 'Today'
                          : key === 'yesterday'
                          ? 'Yesterday'
                          : 'Previous 30 Days'}
                      </Typography>
                      <Typography className={classes.chatCardCount}>
                        {chatData[key].count}
                      </Typography>
                    </Grid>
      
                    <Grid container spacing={2}>
                      {chatData[key].sessions.map((session) => (
                        <Grid item xs={12} sm={6} md={4} key={session.id}>
                          <CardComponent
                            color={
                              key === 'today' ? '#2A7BEC' : key === 'yesterday' ? '#02C170' : '#949494'
                            }
                            onClick={() => handleCardClick(session.id, session.batchNumber, session.isNotification)}
                            title={session.title}
                            date={session.creationDate}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                );
              }
              return null;
            })}
            </Grid>
          </Grid>
        </Grid>
        <Loader loading={loading} />
        <ToasterComponent
          toaster={isToasterOpen}
          message={toasterMessage}
          reqSuccess={reqSuccess}
      />
      </ThemeProvider>
    );
};
    
export const CardComponent = ({ color, onClick, title, date }) => {
    const classes = useStyles();
    return (
    <Grid container className={classes.chatCardContainer} onClick={onClick}>
      <Grid item className={classes.chatCardItem}>
          <IconButton className={classes.chatIconBackground}>
            <ChatCardIcon color={color} />
          </IconButton>
          <Typography variant="body1" className={classes.chatCreated}>created : {date}</Typography>
        </Grid>
        <Typography variant="h6" className={classes.chatCardTitle}>{title}</Typography>
    </Grid>
    );
};